import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponse } from '@angular/common/http';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { saveAs } from 'file-saver';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { ExtracaoDadosService } from 'src/app/shared/services/extracao-dados.service';
import { EscolaService } from 'src/app/shared/services/escola.service';
import { CategoriaService } from 'src/app/shared/services/categoria.service';
import { PsicologoService } from 'src/app/shared/services/psicologo.service';
import { ExtracaoEntidade } from 'src/app/shared/models/extracao-dados/extracaoEntidade';
import { ExtracaoMetadata } from 'src/app/shared/models/extracao-dados/extracaoMetadata';
import { ExtracaoFiltro } from 'src/app/shared/models/extracao-dados/extracaoFiltro';
import { Diretoria } from 'src/app/shared/models/diretoria/Diretoria';
import { Escola } from 'src/app/shared/models/escola/Escola';
import { Categoria } from 'src/app/shared/models/categoria/Categoria';
import { Polo } from 'src/app/shared/models/polo/Polo';
import { Psicologo } from 'src/app/shared/models/psicologo/Psicologo';
import {
  ExtracaoPreviewRequest,
  ExtracaoPreviewResponse,
  ExtracaoFiltroValor
} from 'src/app/shared/models/extracao-dados/extracaoPreviewRequest';

type TipoFiltro = 'ure' | 'escola' | 'status' | 'data' | 'tipo' | 'psicologo' | 'texto';

@Component({
  selector: 'app-extracao-dados',
  templateUrl: './extracao-dados.component.html',
  styleUrls: ['./extracao-dados.component.scss'],
  standalone: true,
  imports: [angularCommonImports, libsCommonImports, convivaCommonImports, NgbPagination]
})
export class ExtracaoDadosComponent implements OnInit {
  private readonly entidadeOcorrenciasId = 3;
  private readonly entidadeAtendimentosPsicologoId = 4;

  private extracaoService = inject(ExtracaoDadosService);
  private escolaService = inject(EscolaService);
  private categoriaService = inject(CategoriaService);
  private psicologoService = inject(PsicologoService);
  private spinnerService = inject(NgxSpinnerService);
  private toastr = inject(ToastrService);
  private convivaErrors = inject(ConvivaErrorHandlingService);
  private destroyRef = inject(DestroyRef);

  public entidades: ExtracaoEntidade[] = [];
  public entidadeSelecionadaId?: number;

  public metadata?: ExtracaoMetadata;

  public filtrosValores: Record<number, any> = {};
  public colunasSelecionadas = new Set<number>();

  public carregandoEntidades = false;
  public entidadesCarregadas = false;
  public inicializandoEntidades = false;
  public carregandoMetadata = false;
  public carregandoPreview = false;
  public exportando = false;

  public diretorias: Diretoria[] = [];
  public escolas: Escola[] = [];
  public categoriasTipo: Categoria[] = [];
  public psicologos: Psicologo[] = [];
  public carregandoDiretorias = false;
  public carregandoEscolas = false;
  public carregandoCategoriasTipo = false;
  public carregandoPsicologos = false;

  public statusOpcoes = [
    { value: 1, label: 'Ativo' },
    { value: 0, label: 'Inativo' }
  ];

  public statusPlanoMelhoriaOpcoes = [
    { value: 0, label: 'Novo' },
    { value: 1, label: 'Aguardando Validação Supervisor de Ensino' },
    { value: 2, label: 'Aguardando Validação PEC CONVIVA' },
    { value: 3, label: 'Devolvido para ajustes' },
    { value: 4, label: 'Aprovado' }
  ];

  public statusPlanoContingenciaOpcoes = [
    { value: 0, label: 'Pendente' },
    { value: 1, label: 'Aprovado' },
    { value: 2, label: 'Reprovado' },
    { value: 3, label: 'Novo' },
    { value: 4, label: 'Aguardando Validação Supervisor de Ensino' },
    { value: 5, label: 'Aguardando Validação PEC CONVIVA' },
    { value: 6, label: 'Devolvido para ajustes' }
  ];

  public opcoesStatus(filtro: ExtracaoFiltro): { value: number; label: string }[] {
    const nome = (filtro?.nome || '').toUpperCase();
    if (nome.includes('PLANO_MELHORIA') || nome.includes('PLANOMELHORIA')) {
      return this.statusPlanoMelhoriaOpcoes;
    }
    if (nome.includes('PLANO_CONTINGENCIA') || nome.includes('PLANOCONTINGENCIA')) {
      return this.statusPlanoContingenciaOpcoes;
    }
    return this.statusOpcoes;
  }

  public previewColunas: { campo: string; label: string }[] = [];
  public previewRegistros: Record<string, any>[] = [];
  public previewTotal = 0;
  public paginaAtual = 1;
  public tamanhoPagina = 10;
  public opcoesTamanhoPagina = [10, 25, 50, 100, 200];
  public buscaRealizada = false;

  public get totalPaginas(): number {
    if (!this.previewTotal || !this.tamanhoPagina) return 0;
    return Math.ceil(this.previewTotal / this.tamanhoPagina);
  }

  public get exibirInicializacaoEntidades(): boolean {
    return this.entidadesCarregadas && !this.carregandoEntidades && this.entidades.length === 0;
  }

  public ngOnInit(): void {
    this.carregarEntidades();
  }

  public carregarEntidades(): void {
    this.carregandoEntidades = true;
    this.entidadesCarregadas = false;
    this.spinnerService.show();
    this.extracaoService.listarEntidades()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lista) => {
          this.entidades = lista ?? [];
          this.entidadesCarregadas = true;
          this.carregandoEntidades = false;
          this.spinnerService.hide();
        },
        error: (err) => {
          this.entidadesCarregadas = false;
          this.carregandoEntidades = false;
          this.spinnerService.hide();
          this.tratarErro(err);
        }
      });
  }

  public inicializarEntidades(): void {
    if (!this.exibirInicializacaoEntidades || this.inicializandoEntidades) return;

    this.inicializandoEntidades = true;
    this.spinnerService.show();

    this.extracaoService.inicializarEntidades()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.toastr.success(response?.message || 'Entidades inicializadas com sucesso.');
          this.inicializandoEntidades = false;
          this.spinnerService.hide();
          window.location.reload();
        },
        error: (err) => {
          this.inicializandoEntidades = false;
          this.spinnerService.hide();
          this.tratarErro(err);
        }
      });
  }

  public onEntidadeChange(): void {
    this.filtrosValores = {};
    this.colunasSelecionadas.clear();
    this.metadata = undefined;
    this.previewColunas = [];
    this.previewRegistros = [];
    this.previewTotal = 0;
    this.paginaAtual = 1;
    this.tamanhoPagina = 10;
    this.buscaRealizada = false;
    this.diretorias = [];
    this.escolas = [];
    this.categoriasTipo = [];
    this.psicologos = [];
    this.carregandoCategoriasTipo = false;
    this.carregandoPsicologos = false;

    if (this.entidadeSelecionadaId == null) {
      return;
    }

    this.carregarMetadata(this.entidadeSelecionadaId);
  }

  private carregarMetadata(entidadeId: number): void {
    this.carregandoMetadata = true;
    this.spinnerService.show();
    this.extracaoService.obterMetadata(entidadeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (meta) => {
          this.metadata = meta;
          this.aplicarColunasPadrao();
          this.carregandoMetadata = false;
          this.spinnerService.hide();
          this.carregarListasAuxiliaresMetadata();
        },
        error: (err) => {
          this.carregandoMetadata = false;
          this.spinnerService.hide();
          this.tratarErro(err);
        }
      });
  }

  private carregarListasAuxiliaresMetadata(): void {
    if (!this.metadata) return;

    const temUre = this.metadata.filtros.some((f) => this.tipoFiltro(f) === 'ure');
    if (temUre) {
      this.carregarDiretorias();
    }

    const temTipoOcorrencia = this.ehEntidadeOcorrencias()
      && this.metadata.filtros.some((f) => this.tipoFiltro(f) === 'tipo');
    if (temTipoOcorrencia) {
      this.carregarCategoriasTipo();
    }
  }

  private carregarDiretorias(): void {
    this.carregandoDiretorias = true;
    this.escolaService.getDiretoriaPorTipoDiretoria()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.carregandoDiretorias = false;
          if (response && response.isSucess) {
            this.diretorias = response.data ?? [];
          } else {
            this.diretorias = [];
            if (response && response.message) {
              this.toastr.error(response.message, 'Erro');
            }
          }
        },
        error: (err) => {
          this.carregandoDiretorias = false;
          this.diretorias = [];
          this.tratarErro(err);
        }
      });
  }

  private carregarEscolas(codigoDiretoria: number): void {
    this.carregandoEscolas = true;
    this.escolaService.getEscolasPorCodigoDiretoria(codigoDiretoria)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.carregandoEscolas = false;
          if (response && response.isSucess) {
            this.escolas = response.data ?? [];
          } else {
            this.escolas = [];
            if (response && response.message) {
              this.toastr.error(response.message, 'Erro');
            }
          }
        },
        error: (err) => {
          this.carregandoEscolas = false;
          this.escolas = [];
          this.tratarErro(err);
        }
      });
  }

  private carregarCategoriasTipo(): void {
    this.carregandoCategoriasTipo = true;
    this.categoriaService.getCategoriasWithSub(1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.carregandoCategoriasTipo = false;
          if (response && response.isSucess) {
            this.categoriasTipo = (response.data ?? [])
              .filter((categoria: Categoria) => categoria.codigoCategoria !== 13);
          } else {
            this.categoriasTipo = [];
            if (response && response.message) {
              this.toastr.error(response.message, response.title || 'Erro');
            }
          }
        },
        error: (err) => {
          this.carregandoCategoriasTipo = false;
          this.categoriasTipo = [];
          this.tratarErro(err);
        }
      });
  }

  private carregarPsicologosPorDiretoria(codigoDiretoria: number): void {
    this.carregandoPsicologos = true;
    this.psicologoService.getPolos(codigoDiretoria)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (!response || !response.isSucess) {
            this.psicologos = [];
            if (response && response.message) {
              this.toastr.error(response.message, 'Erro');
            }
            this.carregandoPsicologos = false;
            return;
          }

          const codigosPolo = this.obterCodigosPolo(response.data);
          if (!codigosPolo.length) {
            this.psicologos = [];
            this.carregandoPsicologos = false;
            return;
          }

          forkJoin(codigosPolo.map((codigoPolo) => this.psicologoService.getPsicologosPorPolo(codigoPolo)))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (responses) => {
                const responseComErro = responses.find((r) => !r || !r.isSucess);
                if (responseComErro?.message) {
                  this.toastr.error(responseComErro.message, 'Erro');
                }

                this.psicologos = this.normalizarPsicologos(
                  responses.flatMap((r) => r?.isSucess ? (r.data ?? []) : [])
                );
                this.carregandoPsicologos = false;
              },
              error: (err) => {
                this.carregandoPsicologos = false;
                this.psicologos = [];
                this.tratarErro(err);
              }
            });
        },
        error: (err) => {
          this.carregandoPsicologos = false;
          this.psicologos = [];
          this.tratarErro(err);
        }
      });
  }

  public tipoFiltro(filtro: ExtracaoFiltro): TipoFiltro {
    const texto = this.normalizarTextoFiltro(filtro);

    if (/(ure|diretoria)/.test(texto)) return 'ure';
    if (/escola/.test(texto)) return 'escola';
    if (this.ehEntidadeOcorrencias() && /\btipo\b/.test(texto)) return 'tipo';
    if (this.ehEntidadeAtendimentosPsicologo() && /psicolog/.test(texto)) return 'psicologo';
    if (/status|situa[cç][aã]o/.test(texto)) return 'status';
    if (/data/.test(texto)) return 'data';
    return 'texto';
  }

  public ehFiltroRaEstudante(filtro: ExtracaoFiltro): boolean {
    const texto = this.normalizarTextoFiltro(filtro);
    const textoCompacto = texto.replace(/\s+/g, '');

    return (/\bra\b/.test(texto) && /estudante/.test(texto))
      || textoCompacto.includes('raestudante')
      || textoCompacto.includes('radoestudante');
  }

  public onFiltroTextoChange(filtro: ExtracaoFiltro, valor: string): void {
    this.filtrosValores[filtro.id] = this.ehFiltroRaEstudante(filtro)
      ? this.normalizarRaEstudante(valor).slice(0, 12)
      : valor;
  }

  private ehEntidadeOcorrencias(): boolean {
    return this.entidadeSelecionadaId === this.entidadeOcorrenciasId
      || this.metadata?.entidade?.id === this.entidadeOcorrenciasId;
  }

  private ehEntidadeAtendimentosPsicologo(): boolean {
    return this.entidadeSelecionadaId === this.entidadeAtendimentosPsicologoId
      || this.metadata?.entidade?.id === this.entidadeAtendimentosPsicologoId;
  }

  public onUreChange(filtro: ExtracaoFiltro): void {
    const codigoDiretoria = this.filtrosValores[filtro.id];
    this.escolas = [];
    this.psicologos = [];

    const filtroEscola = this.metadata?.filtros.find((f) => this.tipoFiltro(f) === 'escola');
    if (filtroEscola) {
      this.filtrosValores[filtroEscola.id] = null;
    }

    const filtroPsicologo = this.metadata?.filtros.find((f) => this.tipoFiltro(f) === 'psicologo');
    if (filtroPsicologo) {
      this.filtrosValores[filtroPsicologo.id] = null;
    }

    if (codigoDiretoria != null && codigoDiretoria !== '') {
      this.carregarEscolas(Number(codigoDiretoria));
      if (filtroPsicologo) {
        this.carregarPsicologosPorDiretoria(Number(codigoDiretoria));
      }
    }
  }

  private aplicarColunasPadrao(): void {
    if (!this.metadata) return;
    this.metadata.colunas
      .filter((c) => c.padrao)
      .forEach((c) => this.colunasSelecionadas.add(c.id));
  }

  public limparFiltros(): void {
    this.filtrosValores = {};
  }

  public selecionarTodasColunas(): void {
    if (!this.metadata) return;
    this.metadata.colunas.forEach((c) => this.colunasSelecionadas.add(c.id));
  }

  public limparSelecaoColunas(): void {
    this.colunasSelecionadas.clear();
  }

  public alternarColuna(id: number, marcado: boolean): void {
    if (marcado) {
      this.colunasSelecionadas.add(id);
    } else {
      this.colunasSelecionadas.delete(id);
    }
  }

  public colunaMarcada(id: number): boolean {
    return this.colunasSelecionadas.has(id);
  }

  private validarParaConsulta(): boolean {
    if (!this.metadata || this.entidadeSelecionadaId == null) {
      this.toastr.warning('Selecione uma entidade.');
      return false;
    }

    if (this.colunasSelecionadas.size === 0) {
      this.toastr.warning('Selecione ao menos uma coluna.');
      return false;
    }

    const obrigatoriosFaltando = this.metadata.filtros
      .filter((f) => f.obrigatorio)
      .filter((f) => !this.ehFiltroRaEstudante(f))
      .filter((f) => !this.valorFiltroPreenchido(this.filtrosValores[f.id]));

    if (obrigatoriosFaltando.length > 0) {
      this.toastr.warning(`Preencha o(s) filtro(s) obrigatório(s): ${obrigatoriosFaltando.map((f) => f.label).join(', ')}.`);
      return false;
    }

    const filtroRaInvalido = this.metadata.filtros.find((f) => {
      const valor = this.normalizarRaEstudante(this.filtrosValores[f.id]);
      return this.ehFiltroRaEstudante(f) && valor.length > 0 && valor.length !== 12;
    });

    if (filtroRaInvalido) {
      this.toastr.warning(`${filtroRaInvalido.label} deve conter 12 numeros.`);
      return false;
    }

    return true;
  }

  private montarPayload(): ExtracaoPreviewRequest {
    const filtros: ExtracaoFiltroValor[] = Object.entries(this.filtrosValores)
      .map(([id, valor]) => ({
        filtroId: Number(id),
        valor: this.normalizarValorFiltro(Number(id), valor)
      }))
      .filter(({ valor }) => valor !== '');

    return {
      entidadeId: this.entidadeSelecionadaId!,
      colunas: Array.from(this.colunasSelecionadas),
      filtros,
      pagina: this.paginaAtual,
      tamanhoPagina: this.tamanhoPagina
    };
  }

  private normalizarValorFiltro(id: number, valor: any): string {
    if (valor == null) return '';

    const filtro = this.metadata?.filtros.find((f) => f.id === id);
    if (filtro && this.ehFiltroRaEstudante(filtro)) {
      return this.normalizarRaEstudante(valor);
    }

    return `${valor}`.trim();
  }

  private obterCodigosPolo(valor: Polo | Polo[] | null | undefined): number[] {
    const polos = Array.isArray(valor) ? valor : valor ? [valor] : [];
    const codigos = polos
      .map((polo) => Number(polo.codigoPolo))
      .filter((codigo) => Number.isFinite(codigo) && codigo > 0);

    return Array.from(new Set(codigos));
  }

  private normalizarPsicologos(psicologos: Psicologo[]): Psicologo[] {
    const psicologosPorCodigo = new Map<number, Psicologo>();

    psicologos.forEach((psicologo) => {
      const codigoPsicologo = Number(psicologo.codigoPsicologo);
      if (!Number.isFinite(codigoPsicologo) || codigoPsicologo <= 0 || psicologosPorCodigo.has(codigoPsicologo)) return;

      psicologosPorCodigo.set(codigoPsicologo, {
        ...psicologo,
        codigoPsicologo,
        nomePsicologoCompleto: [psicologo.codigoCPF, psicologo.nomePsicologo]
          .filter(Boolean)
          .join(' - ')
      });
    });

    return Array.from(psicologosPorCodigo.values());
  }

  private valorFiltroPreenchido(valor: any): boolean {
    return valor != null && `${valor}`.trim() !== '';
  }

  private normalizarRaEstudante(valor: any): string {
    return `${valor ?? ''}`.replace(/\D/g, '');
  }

  private normalizarTextoFiltro(filtro: ExtracaoFiltro): string {
    return `${filtro?.nome || ''} ${filtro?.label || ''}`
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[_-]/g, ' ');
  }

  private montarPayloadExportacao(): ExtracaoPreviewRequest {
    return {
      ...this.montarPayload(),
      pagina: 1,
      tamanhoPagina: this.tamanhoPagina
    };
  }

  public buscar(): void {
    if (!this.validarParaConsulta()) return;

    this.paginaAtual = 1;
    this.executarPreview();
  }

  public onPaginaChange(novaPagina: number): void {
    if (novaPagina === this.paginaAtual) return;
    if (!this.validarParaConsulta()) return;

    this.paginaAtual = novaPagina;
    this.executarPreview();
  }

  public onTamanhoPaginaChange(): void {
    this.paginaAtual = 1;
    if (this.buscaRealizada) {
      if (!this.validarParaConsulta()) return;
      this.executarPreview();
    }
  }

  private executarPreview(): void {
    this.carregandoPreview = true;
    this.spinnerService.show();

    this.extracaoService.preview(this.montarPayload())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: ExtracaoPreviewResponse) => {
          this.previewColunas = resp?.colunas ?? [];
          this.previewRegistros = resp?.registros ?? [];
          this.previewTotal = resp?.total ?? 0;
          this.buscaRealizada = true;
          this.carregandoPreview = false;
          this.spinnerService.hide();
        },
        error: (err) => {
          this.carregandoPreview = false;
          this.spinnerService.hide();
          this.tratarErro(err);
        }
      });
  }

  public exportarCsv(): void {
    if (!this.validarParaConsulta()) return;
    this.exportar('csv');
  }

  public exportarExcel(): void {
    if (!this.validarParaConsulta()) return;
    this.exportar('excel');
  }

  private exportar(formato: 'csv' | 'excel'): void {
    this.exportando = true;
    this.spinnerService.show();

    const payload = this.montarPayloadExportacao();
    const obs = formato === 'csv'
      ? this.extracaoService.exportarCsv(payload)
      : this.extracaoService.exportarExcel(payload);

    obs.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (!response.body) {
            this.toastr.error('Arquivo nao retornado pela API.', 'Erro');
            this.exportando = false;
            this.spinnerService.hide();
            return;
          }

          const extensao = formato === 'csv' ? 'csv' : 'xlsx';
          const nomeArquivo = this.obterNomeArquivoResposta(response, extensao);
          this.baixarArquivo(response.body, nomeArquivo);
          this.toastr.success('Arquivo gerado com sucesso.');
          this.exportando = false;
          this.spinnerService.hide();
        },
        error: (err) => {
          this.exportando = false;
          this.spinnerService.hide();
          this.tratarErro(err);
        }
      });
  }

  private baixarArquivo(blob: Blob, nomeArquivo: string): void {
    saveAs(blob, nomeArquivo);
  }

  private tratarErro(err: any): void {
    if (err?.error instanceof Blob) {
      this.tratarErroBlob(err);
      this.convivaErrors.handleResultError(err);
      return;
    }

    const mensagem = this.extrairMensagemErro(err);
    if (mensagem) {
      this.toastr.error(mensagem, 'Erro');
    } else {
      this.toastr.error('Ocorreu um erro ao processar a requisição.', 'Erro');
    }
    this.convivaErrors.handleResultError(err);
  }

  private tratarErroBlob(err: any): void {
    err.error.text()
      .then((texto: string) => {
        const mensagem = this.extrairMensagemErroBlob(texto) || err?.message;
        this.toastr.error(mensagem || 'Ocorreu um erro ao processar a requisicao.', 'Erro');
      })
      .catch(() => this.toastr.error('Ocorreu um erro ao processar a requisicao.', 'Erro'));
  }

  private extrairMensagemErro(err: any): string | null {
    return err?.error?.message || err?.error?.mensagem || err?.message || err?.mensagem || null;
  }

  private extrairMensagemErroBlob(texto: string): string | null {
    if (!texto) return null;

    try {
      const erro = JSON.parse(texto);
      return erro?.message || erro?.mensagem || erro?.title || null;
    } catch {
      return texto;
    }
  }

  private obterNomeArquivoResposta(response: HttpResponse<Blob>, extensao: 'csv' | 'xlsx'): string {
    const contentDisposition = response.headers.get('Content-Disposition');
    return this.extrairNomeArquivoContentDisposition(contentDisposition)
      || this.obterNomeArquivoFallback(extensao);
  }

  private extrairNomeArquivoContentDisposition(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;

    const filenameStar = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
    if (filenameStar?.[1]) {
      return this.decifrarNomeArquivo(filenameStar[1]);
    }

    const filenameQuoted = /filename\s*=\s*"([^"]+)"/i.exec(contentDisposition);
    if (filenameQuoted?.[1]) {
      return filenameQuoted[1].trim();
    }

    const filename = /filename\s*=\s*([^;]+)/i.exec(contentDisposition);
    return filename?.[1]?.trim() || null;
  }

  private decifrarNomeArquivo(valor: string): string {
    const nome = valor.trim().replace(/^["']|["']$/g, '');
    try {
      return decodeURIComponent(nome);
    } catch {
      return nome;
    }
  }

  private obterNomeArquivoFallback(extensao: 'csv' | 'xlsx'): string {
    const entidade = this.metadata?.entidade?.label || 'dados';
    return `extracao-${entidade}-${new Date().getTime()}.${extensao}`;
  }
}
